import type { DedupCandidate, DedupCandidateContact } from "@adva/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/v2/empty-state";
import { HIGH_CONFIDENCE_THRESHOLD, confidenceTone } from "../labels";

export type DedupAction = "merge_a" | "merge_b" | "keep_both" | "dismiss";

interface DuplicatesTabProps {
  candidates: DedupCandidate[];
  resolutions: Map<string, DedupAction>;
  onResolve: (candidateId: string, action: DedupAction) => void;
  onMergeAllHighConfidence: () => void;
  onDismissAll: () => void;
  disabled?: boolean;
  bulkBusy?: boolean;
}

function ContactCell({
  label,
  contact,
}: {
  label: string;
  contact: DedupCandidateContact;
}) {
  const name =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
    "(unnamed)";
  return (
    <div className="space-y-0.5">
      <div className="typo-label text-[color:var(--color-text-md)]">
        {label}
      </div>
      <div className="font-medium text-[color:var(--color-text-hi)]">
        {name}
      </div>
      {contact.email && (
        <div className="typo-body text-[color:var(--color-text-md)]">
          {contact.email}
        </div>
      )}
      {contact.phone && (
        <div className="typo-body text-[color:var(--color-text-md)]">
          {contact.phone}
        </div>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const tone = confidenceTone(confidence);
  const colorMap: Record<typeof tone, string> = {
    success:
      "border-[color:var(--color-success)] text-[color:var(--color-success)]",
    warning:
      "border-[color:var(--color-warning)] text-[color:var(--color-warning)]",
    neutral:
      "border-[color:var(--color-border)] text-[color:var(--color-text-md)]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label tabular-nums ${colorMap[tone]}`}
    >
      {Math.round(confidence * 100)}% match
    </span>
  );
}

const ACTION_LABELS: Record<DedupAction, string> = {
  merge_a: "Merge — keep first",
  merge_b: "Merge — keep second",
  keep_both: "Keep both",
  dismiss: "Dismiss",
};

export function DuplicatesTab({
  candidates,
  resolutions,
  onResolve,
  onMergeAllHighConfidence,
  onDismissAll,
  disabled = false,
  bulkBusy = false,
}: DuplicatesTabProps) {
  if (candidates.length === 0) {
    return (
      <EmptyState
        title="No duplicates detected"
        description="No imported contacts matched existing records closely enough to flag as duplicates."
      />
    );
  }

  const highConfidenceCount = candidates.filter(
    (c) => c.confidence >= HIGH_CONFIDENCE_THRESHOLD,
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="typo-label text-[color:var(--color-text-md)]">
          {candidates.length} candidate{" "}
          {candidates.length === 1 ? "pair" : "pairs"}
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onMergeAllHighConfidence}
            disabled={disabled || highConfidenceCount === 0 || bulkBusy}
          >
            Merge all &gt;90% ({highConfidenceCount})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDismissAll}
            disabled={disabled || bulkBusy}
          >
            Dismiss all
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        {candidates.map((c) => {
          const action = resolutions.get(c.id);
          return (
            <Card key={c.id} className="p-4">
              <div className="mb-3 flex items-center gap-3">
                <ConfidenceBadge confidence={c.confidence} />
                {c.match_fields.map((f: string) => (
                  <Badge key={f} variant="secondary" className="typo-label">
                    {f.replace("_", " ")}
                  </Badge>
                ))}
                {action && (
                  <Badge
                    variant="outline"
                    className="ml-auto typo-label text-[color:var(--color-accent)]"
                  >
                    {ACTION_LABELS[action]}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ContactCell label="Imported" contact={c.imported_contact} />
                <ContactCell label="Existing" contact={c.existing_contact} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(["merge_a", "merge_b", "keep_both", "dismiss"] as const).map(
                  (a) => (
                    <Button
                      key={a}
                      size="sm"
                      variant={action === a ? "default" : "outline"}
                      onClick={() => onResolve(c.id, a)}
                      disabled={disabled}
                    >
                      {ACTION_LABELS[a]}
                    </Button>
                  ),
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
